import React from "react";
import * as Sentry from "@sentry/react";
import { resolveSentryEnvironment } from "./sentry-environment";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";

function safeStringify(value) {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

// RTK Query rejections and plain-object throws are not Error instances.
// The wrappers already build a generic Error message to avoid opaque
// "Object captured as exception" titles, but the raw payload is then
// discarded. This helper builds the same fallback message AND returns the
// raw value separately so the wrappers can attach it as extra.originalError,
// preserving the actual status / code / message on the Sentry event.
function normalizeErrorForSentry(value, fallbackMessage) {
  if (value instanceof Error) {
    return { error: value, originalError: undefined };
  }

  let message;
  if (value === null || value === undefined) {
    message = fallbackMessage || "Captured non-error value (null/undefined)";
  } else if (typeof value === "string") {
    message = value;
  } else if (typeof value === "object") {
    if (typeof value.message === "string" && value.message.length > 0) {
      message = value.message;
      if (value.status !== undefined) message = `[${value.status}] ${message}`;
    } else if (value.status !== undefined) {
      const inner = typeof value.error === "string" ? value.error : "";
      message = inner
        ? `API error (status=${value.status}): ${inner}`
        : `API error (status=${value.status})`;
    } else {
      message = fallbackMessage || `Captured non-error object: ${safeStringify(value)}`;
    }
  } else {
    message = `Captured non-error value: ${String(value)}`;
  }

  return { error: new Error(message), originalError: value };
}

const DSN = process.env.REACT_APP_SENTRY_DSN || "";
const API_HOST = process.env.REACT_APP_API_HOST || "";
// The backend this build talks to decides the environment. NODE_ENV says
// "production" for any CRA production build, including one made against the
// dev API, so it cannot keep QA events out of the live issue list.
const SENTRY_ENVIRONMENT = resolveSentryEnvironment(API_HOST);
const IS_PROD = SENTRY_ENVIRONMENT === "production";
const ENABLED_IN_NON_PROD = process.env.REACT_APP_ENABLE_SENTRY === "true";
const SHOULD_INIT = Boolean(DSN) && (IS_PROD || ENABLED_IN_NON_PROD);

export function initSentry() {
  if (!SHOULD_INIT) return;

  Sentry.init({
    dsn: DSN,
    environment: SENTRY_ENVIRONMENT,
    release: `cafe-web-admin@${process.env.REACT_APP_VERSION || "0.1.0"}`,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: IS_PROD ? 0.05 : 0,
    tracePropagationTargets: [/^\//, ...(API_HOST ? [API_HOST] : [])],
    replaysSessionSampleRate: IS_PROD ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: true,
  });
}

export function setSentryUser(userData) {
  Sentry.setUser({
    id: userData?.id,
    email: userData?.email,
    username: userData?.username,
    ip_address: "{{auto}}",
  });
}

export function clearSentryUser() {
  Sentry.setUser(null);
}

export function setSentrySiteContext(site) {
  if (!site) return;
  Sentry.setTag("site.id", site.id);
  Sentry.setContext("site", site);
}

export function captureApiError(error, { endpoint, args, status, data } = {}) {
  if (status === 401) return;

  const level = status >= 500 ? "error" : "warning";
  const { error: normalized, originalError } = normalizeErrorForSentry(
    error,
    `API error: ${endpoint} (${status})`
  );

  Sentry.withScope((scope) => {
    scope.setLevel(level);
    scope.setTag("api.endpoint", endpoint);
    scope.setTag("api.status", status);
    scope.setContext("api", { endpoint, args, status, data });
    if (originalError !== undefined) {
      Sentry.captureException(normalized, { extra: { originalError } });
    } else {
      Sentry.captureException(normalized);
    }
  });
}

export function captureComponentError(error, { component, action, extra, level = "error" } = {}) {
  const { error: normalized, originalError } = normalizeErrorForSentry(
    error,
    `${component}: ${action} failed`
  );

  Sentry.withScope((scope) => {
    scope.setLevel(level);
    scope.setTag("component", component);
    scope.setTag("action", action);
    if (extra) scope.setContext("extra", extra);
    if (originalError !== undefined) {
      const mergedExtra = extra ? { ...extra, originalError } : { originalError };
      Sentry.captureException(normalized, { extra: mergedExtra });
    } else {
      Sentry.captureException(normalized);
    }
  });
}
